import { z } from "zod";
import moment from "moment";
import { getAge } from "./getAge";
var isBlank = function (val) { return !val || val.trim() === ""; };
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var isEmptyContactRow = function (contact) {
    return isBlank(contact === null || contact === void 0 ? void 0 : contact.type) && isBlank(contact === null || contact === void 0 ? void 0 : contact.value) && isBlank(contact === null || contact === void 0 ? void 0 : contact.info);
};
export var isEmptyGuarantor = function (guarantor) {
    var _a;
    return !guarantor ||
        (isBlank(guarantor.firstName) &&
            isBlank(guarantor.lastName) &&
            isBlank(guarantor.email) &&
            isBlank(guarantor.titlesBefore) &&
            isBlank(guarantor.titlesAfter) &&
            ((_a = guarantor.contacts) !== null && _a !== void 0 ? _a : []).every(isEmptyContactRow));
};
export var isEmptyAlternativeName = function (alternativeName) {
    return !alternativeName ||
        (isBlank(alternativeName.name) &&
            isBlank(alternativeName.nameSuffix) &&
            isBlank(alternativeName.qualifier) &&
            !alternativeName.type);
};
var createContactSchema = function (formatMessage) {
    return z
        .object({
        type: z.union([
            z.literal(""),
            z.enum(["EMAIL", "WEB", "PHONE", "DATA_BOX"]),
        ]),
        value: z.string(),
        info: z.string().optional(),
    })
        .superRefine(function (contact, ctx) {
        if (contact.type === "" && contact.value === "")
            return;
        if (contact.type !== "" && contact.value === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: formatMessage({
                    id: "stripes-core.publishers.validation.contact.value",
                }),
                path: ["value"],
            });
            return;
        }
        if (contact.type === "" && contact.value !== "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: formatMessage({
                    id: "stripes-core.publishers.validation.contact.type",
                }),
                path: ["type"],
            });
            return;
        }
        if (contact.type === "EMAIL") {
            if (!EMAIL_REGEX.test(contact.value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.contact.email",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === "WEB") {
            var url = contact.value.trim();
            if (url && !/^https?:\/\//i.test(url)) {
                url = "https://".concat(url);
                contact.value = url;
            }
            if (!/^https?:\/\/[^\s$.?#]+\.[^\s]+$/.test(url)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.contact.web",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === "PHONE") {
            var input = contact.value.trim();
            var hasPlus = input.startsWith("+");
            var digitsOnly = input.replace(/\D/g, "");
            if (digitsOnly.length < 9) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
            else if (digitsOnly.length === 9) {
                var formatted = "".concat(digitsOnly.slice(0, 3), " ").concat(digitsOnly.slice(3, 6), " ").concat(digitsOnly.slice(6));
                if (contact.value !== formatted) {
                    contact.value = formatted;
                }
            }
            else if (hasPlus && digitsOnly.length >= 10) {
                if (digitsOnly.length > 15) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "stripes-core.publishers.validation.contact.phone.e164",
                        }),
                        path: ["value"],
                    });
                }
                else {
                    var e164Formatted = "+".concat(digitsOnly);
                    if (contact.value !== e164Formatted) {
                        contact.value = e164Formatted;
                    }
                }
            }
            else {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === "DATA_BOX") {
            if (contact.value.length < 6 || contact.value.length > 12) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.contact.dataBox",
                    }),
                    path: ["value"],
                });
            }
        }
    });
};
var createAddressSchema = function (formatMessage) {
    return z.object({
        addressCode: z.preprocess(function (val) { return (typeof val === "string" && val.trim() === "" ? "" : val); }, z.string().optional().default("")),
        street: z.string().min(1, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.address.street",
            }),
        }),
        municipality: z.string().min(1, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.address.municipality",
            }),
        }),
        postalCode: z.string().min(1, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.address.postalCode",
            }),
        }),
        region: z.object({
            id: z.string().min(1, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.address.region",
                }),
            }),
            name: z.string().optional(),
        }),
    });
};
export var createPublisherSchema = function (formatMessage) {
    return z
        .object({
        legalForm: z.enum(["LEGAL", "NATURAL", "UNKNOWN"], {
            required_error: formatMessage({
                id: "stripes-core.publishers.validation.general.legalForm",
            }, { age: 0 }),
        }),
        registrationAgency: z
            .enum(["ISBN", "ISMN", ""], {
            required_error: formatMessage({
                id: "stripes-core.publishers.validation.general.registrationAgency",
            }),
        })
            .refine(function (val) { return val !== ""; }, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.general.registrationAgency",
            }),
        }),
        registrationIntent: z
            .enum(["ONE_OFF", "CONTINUOUS", ""], {
            required_error: formatMessage({
                id: "stripes-core.publishers.validation.general.registrationIntent",
            }),
        })
            .refine(function (val) { return val !== ""; }, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.general.registrationIntent",
            }),
        }),
        version: z.number().optional(),
        id: z.string().optional(),
        nameSuffix: z.string().optional(),
        name: z.string().min(1, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.general.name",
            }),
        }),
        status: z.enum(["REQUEST", "REJECTED", "ACTIVE", "CLOSING", "CLOSED"], {
            required_error: "Status is required",
        }),
        companyNumber: z.preprocess(function (arg) { return (typeof arg === "string" ? arg.trim() : arg); }, z
            .union([
            z.literal(""),
            z
                .string()
                .min(8, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.general.companyNumber",
                }),
            })
                .max(8, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.general.companyNumber",
                }),
            }),
        ])
            .optional()),
        taxNumber: z
            .union([
            z.literal(""),
            z.string().refine(function (val) { return /^[A-Za-z]{2}\d{2,13}$/.test(val); }, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.general.taxNumber",
                }),
            }),
        ])
            .optional(),
        birthDate: z
            .union([
            z.literal(""),
            z
                .string()
                .refine(function (val) { return moment(val, "DD/MM/YYYY", true).isValid(); }, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.general.birthDate.format",
                }),
            }),
        ])
            .optional()
            .transform(function (val) {
            if (!val || val === "")
                return "";
            var parsed = moment(val, "DD/MM/YYYY", true);
            return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        })
            .superRefine(function (val, ctx) {
            if (!val)
                return;
            var m = moment(val, "YYYY-MM-DD", true);
            if (!m.isValid())
                return;
            var min = moment("1900-01-01", "YYYY-MM-DD", true);
            var max = moment().endOf("day");
            if (m.isBefore(min)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.general.birthDate.min",
                    }),
                    path: [],
                });
            }
            if (m.isAfter(max)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.general.birthDate.max",
                    }),
                    path: [],
                });
            }
        }),
        mainAddressPublishConsent: z.boolean().default(true),
        mailingAddressPublishConsent: z.boolean().default(true).optional(),
        mainAddress: createAddressSchema(formatMessage),
        mailingAddress: z
            .object({
            street: z.string().optional(),
            postalCode: z.string().optional(),
            municipality: z.string().optional(),
            region: z
                .object({
                id: z.string().optional(),
                name: z.string().optional(),
            })
                .optional(),
        })
            .optional()
            .transform(function (value) {
            if (!value)
                return undefined;
            var street = value.street, postalCode = value.postalCode, municipality = value.municipality, region = value.region;
            var regionId = (region === null || region === void 0 ? void 0 : region.id) || "";
            if ((!street || !street.trim()) &&
                (!postalCode || !postalCode.trim()) &&
                (!municipality || !municipality.trim()) &&
                (!regionId || !regionId.trim())) {
                return undefined;
            }
            return {
                street: street || "",
                postalCode: postalCode || "",
                municipality: municipality || "",
                region: {
                    id: (region === null || region === void 0 ? void 0 : region.id) || "",
                    name: (region === null || region === void 0 ? void 0 : region.name) || "",
                },
            };
        }),
        contacts: z.array(createContactSchema(formatMessage)).optional(),
        alternativeNames: z
            .array(z
            .object({
            name: z.string(),
            nameSuffix: z
                .string()
                .max(50, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.alternativeNames.nameSuffix",
                }),
            })
                .optional(),
            qualifier: z
                .string()
                .max(50, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.alternativeNames.qualifier",
                }),
            })
                .optional(),
            type: z
                .enum([
                "PREVIOUS",
                "IMPRINT",
                "ABBREVIATION",
                "ORGANIZATION_UNIT",
                "FOREIGN_LANGUAGE",
            ])
                .optional(),
            nonPublic: z.boolean().default(false),
        })
            .superRefine(function (alternativeName, ctx) {
            if (isEmptyAlternativeName(alternativeName))
                return;
            if (isBlank(alternativeName.name)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.alternativeNames.fillOrRemove",
                    }),
                    path: ["name"],
                });
            }
        }))
            .optional(),
        guarantors: z
            .array(z
            .object({
            firstName: z.string(),
            lastName: z.string(),
            email: z.string(),
            contacts: z.array(createContactSchema(formatMessage)).optional(),
            titlesAfter: z.string().optional(),
            titlesBefore: z.string().optional(),
        })
            .superRefine(function (guarantor, ctx) {
            if (isEmptyGuarantor(guarantor))
                return;
            var fillOrRemoveMessage = formatMessage({
                id: "stripes-core.publishers.validation.guarantors.fillOrRemove",
            });
            if (isBlank(guarantor.firstName)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: fillOrRemoveMessage,
                    path: ["firstName"],
                });
            }
            if (isBlank(guarantor.lastName)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: fillOrRemoveMessage,
                    path: ["lastName"],
                });
            }
            if (isBlank(guarantor.email)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: fillOrRemoveMessage,
                    path: ["email"],
                });
            }
            else if (!EMAIL_REGEX.test(guarantor.email)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.guarantors.email.format",
                    }),
                    path: ["email"],
                });
            }
        }))
            .optional(),
        internalComment: z.string().optional(),
        workSpecification: z.object({
            title: z.string().optional(),
            subtitle: z.string().optional(),
            volumeNumber: z.string().optional(),
            partTitle: z.string().optional(),
            authors: z
                .array(z.object({
                firstName: z.string(),
                lastName: z.string(),
                // NK authority the author was picked from (DB-804)
                personId: z.string().optional(),
                sourceId: z.string().optional(),
            }))
                .optional(),
            publicationDate: z.string().optional(),
            editionNumber: z.string().optional(),
            coEditionWith: z.string().optional(),
            publicationForm: z.string().optional(),
            publicationType: z
                .object({
                printedPublications: z.preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string())),
            })
                .optional(),
            eBookFormats: z.preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string())),
            otherFormats: z.preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.object({
                format: z.string(),
            }))),
            dataCarrierVersions: z.preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string())),
            audioBookFormats: z.preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string())),
        }),
        website: z.string().optional(),
    })
        .superRefine(function (data, ctx) {
        var _a, _b, _c, _d, _e, _f;
        var hasFilledGuarantor = ((_a = data.guarantors) !== null && _a !== void 0 ? _a : []).some(function (guarantor) { return !isEmptyGuarantor(guarantor); });
        if (!hasFilledGuarantor) {
            var message_1 = formatMessage({
                id: "stripes-core.publishers.validation.guarantors.atLeastOne",
            });
            ["firstName", "lastName", "email"].forEach(function (field) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message_1,
                    path: ["guarantors", 0, field],
                });
            });
        }
        if (data.mailingAddress) {
            var _g = data.mailingAddress, street = _g.street, postalCode = _g.postalCode, municipality = _g.municipality, region = _g.region;
            if (!street.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.address.street",
                    }),
                    path: ["mailingAddress", "street"],
                });
            }
            if (!/^\d{3} ?\d{2}$/.test(postalCode)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.address.postalCode",
                    }),
                    path: ["mailingAddress", "postalCode"],
                });
            }
            if (!municipality.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.address.municipality",
                    }),
                    path: ["mailingAddress", "municipality"],
                });
            }
            if (!(region === null || region === void 0 ? void 0 : region.id.trim())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.address.region",
                    }),
                    path: ["mailingAddress", "region", "id"],
                });
            }
        }
        if (data.legalForm === "NATURAL") {
            var hasBirthDate = ((_b = data.birthDate) === null || _b === void 0 ? void 0 : _b.trim()) !== "";
            var hasIco = ((_c = data.companyNumber) === null || _c === void 0 ? void 0 : _c.trim()) !== "";
            var hasDic = ((_d = data.taxNumber) === null || _d === void 0 ? void 0 : _d.trim()) !== "";
            if (!hasBirthDate && !hasIco && !hasDic) {
                var message = formatMessage({
                    id: "stripes-core.publishers.validation.general.legalForm.natural",
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["birthDate"],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["companyNumber"],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["taxNumber"],
                });
            }
        }
        else if (data.legalForm === "LEGAL") {
            var hasIco = ((_e = data.companyNumber) === null || _e === void 0 ? void 0 : _e.trim()) !== "";
            var hasDic = ((_f = data.taxNumber) === null || _f === void 0 ? void 0 : _f.trim()) !== "";
            if (!hasIco && !hasDic) {
                var message = formatMessage({
                    id: "stripes-core.publishers.validation.general.legalForm.legal",
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["companyNumber"],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["taxNumber"],
                });
            }
        }
        if (data.legalForm === "NATURAL" &&
            data.birthDate &&
            data.birthDate.trim() !== "") {
            var age = getAge(data.birthDate);
            if (age < 18) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.general.birthDate.age",
                    }, { age: age }),
                    path: ["birthDate"],
                });
            }
        }
        if (data.registrationIntent === "ONE_OFF") {
            var ws = data.workSpecification;
            if (!ws) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "stripes-core.publishers.validation.workSpec.required",
                        defaultMessage: "Work specification is required for one-time assignment.",
                    }),
                    path: ["workSpecification"],
                });
            }
            else {
                if (!ws.title || ws.title.trim() === "") {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "stripes-core.publishers.validation.workSpec.title",
                        }),
                        path: ["workSpecification", "title"],
                    });
                }
                if (!ws.publicationDate || ws.publicationDate.trim() === "") {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "stripes-core.publishers.validation.workSpec.publicationDate",
                        }),
                        path: ["workSpecification", "publicationDate"],
                    });
                }
                if (!ws.editionNumber || ws.editionNumber.trim() === "") {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "stripes-core.publishers.validation.workSpec.editionNumber",
                        }),
                        path: ["workSpecification", "editionNumber"],
                    });
                }
                if (!ws.publicationForm || ws.publicationForm.trim() === "") {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "stripes-core.publishers.validation.workSpec.publicationForm",
                        }),
                        path: ["workSpecification", "publicationForm"],
                    });
                }
            }
        }
    });
};
export var createAresSchema = function (formatMessage) {
    return z.object({
        name: z.string().min(1, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.general.name",
            }),
        }),
        mainAddress: createAddressSchema(formatMessage),
        companyNumber: z
            .string()
            .optional()
            .refine(function (val) { return !val || /^\d{8}$/.test(val); }, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.general.companyNumber",
            }),
        }),
        taxNumber: z
            .string()
            .optional()
            .refine(function (val) {
            return !val || (val.length >= 2 && val.length <= 13 && /^\d+$/.test(val));
        }, {
            message: formatMessage({
                id: "stripes-core.publishers.validation.general.taxNumber",
            }),
        }),
        alternativeNames: z
            .array(z.object({
            name: z.string().min(1, {
                message: formatMessage({
                    id: "stripes-core.publishers.validation.alternativeNames.name",
                }),
            }),
        }))
            .optional(),
    });
};
export var validatePublisher = function (data, formatMessage) { return createPublisherSchema(formatMessage).safeParse(data); };
export var validateAres = function (data, formatMessage) {
    return createAresSchema(formatMessage).safeParse(data);
};
